import { getFunctions, httpsCallable } from 'firebase/functions';
import { createLogger, getLogs, clearLogs, getErrorLogs, type StoredLog, type Severity } from '@/utils/debug';

// Re-export Severity type for use by other modules
export type { Severity } from '@/utils/debug';

const functions = getFunctions();
const log = createLogger('Diagnostics');

// ===========================================
// Types
// ===========================================

export interface CloudLogEntry {
  id: string;
  timestamp: string;
  level: string;
  severity: Severity;
  module: string;
  action: string;
  message?: string;
  functionName?: string;
  data?: Record<string, unknown>;
  source: 'cloud-functions';
}

export interface LogEntry {
  id: string;
  timestamp: string;
  level: string;
  severity: Severity;
  module: string;
  action: string;
  message?: string;
  stack?: string;
  functionName?: string;
  data?: Record<string, unknown>;
  source: 'pwa' | 'cloud-functions';
  duration?: number;
}

interface GetLogsInput {
  hours?: number;
  minSeverity?: Severity;
  functionName?: string;
  limit?: number;
}

interface GetLogsOutput {
  success: boolean;
  logs?: CloudLogEntry[];
  error?: string;
}

interface LogStatsOutput {
  success: boolean;
  stats?: {
    total: number;
    bySeverity: Record<Severity, number>;
    byFunction: Record<string, number>;
    lastHour: number;
    last24Hours: number;
  };
  error?: string;
}

export interface CombinedLogStats {
  pwa: {
    total: number;
    errors: number;
    warnings: number;
  };
  cloudFunctions: {
    total: number;
    bySeverity: Record<Severity, number>;
    byFunction: Record<string, number>;
    lastHour: number;
    last24Hours: number;
  } | null;
}

// ===========================================
// PWA Log Functions
// ===========================================

/**
 * Get all PWA logs
 */
export function getPwaLogs(): StoredLog[] {
  return getLogs();
}

/**
 * Get PWA error/warning logs only
 */
export function getPwaErrorLogs(): StoredLog[] {
  return getErrorLogs();
}

/**
 * Clear all PWA logs
 */
export function clearPwaLogs(): void {
  clearLogs();
  log.info('PWA logs cleared');
}

// ===========================================
// Cloud Function Log Functions
// ===========================================

/**
 * Fetch logs from Cloud Functions
 */
export async function getCloudFunctionLogs(options: GetLogsInput = {}): Promise<CloudLogEntry[]> {
  return log.operation('getCloudFunctionLogs', async () => {
    const getFunctionLogs = httpsCallable<Record<string, unknown>, GetLogsOutput>(
      functions,
      'crm_getFunctionLogs'
    );

    const result = await getFunctionLogs(options as Record<string, unknown>);

    if (!result.data.success) {
      throw new Error(result.data.error || 'Failed to fetch logs');
    }

    log.debug('getCloudFunctionLogs result', { count: result.data.logs?.length ?? 0 });
    return result.data.logs || [];
  }, options as Record<string, unknown>);
}

/**
 * Get Cloud Function log statistics
 */
export async function getCloudLogStats(): Promise<LogStatsOutput['stats'] | null> {
  return log.operation('getCloudLogStats', async () => {
    const getLogStats = httpsCallable<unknown, LogStatsOutput>(
      functions,
      'crm_getLogStats'
    );

    const result = await getLogStats({});

    if (!result.data.success) {
      log.warn('getCloudLogStats failed', { error: result.data.error });
      return null;
    }

    return result.data.stats || null;
  });
}

// ===========================================
// Combined Log Functions
// ===========================================

/**
 * Get all logs from both PWA and Cloud Functions, combined and sorted
 */
export async function getAllLogs(options: GetLogsInput = {}): Promise<LogEntry[]> {
  const pwaLogs = getPwaLogs();
  let cloudLogs: CloudLogEntry[] = [];

  try {
    cloudLogs = await getCloudFunctionLogs(options);
  } catch (error) {
    log.warn('Failed to fetch cloud logs, showing PWA logs only', { error });
  }

  // Combine and normalize
  const allLogs: LogEntry[] = [
    ...pwaLogs.map(l => ({ ...l, level: l.level.toUpperCase() })),
    ...cloudLogs,
  ];

  // Sort by timestamp descending
  allLogs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  // Filter by severity if specified
  if (options.minSeverity) {
    const severityOrder: Severity[] = ['low', 'medium', 'high', 'critical'];
    const minIndex = severityOrder.indexOf(options.minSeverity);
    return allLogs.filter(log => severityOrder.indexOf(log.severity) >= minIndex);
  }

  return allLogs;
}

/**
 * Get combined statistics from both sources
 */
export async function getCombinedStats(): Promise<CombinedLogStats> {
  const pwaLogs = getPwaLogs();
  const pwaErrors = pwaLogs.filter(l => l.level === 'error');
  const pwaWarnings = pwaLogs.filter(l => l.level === 'warn');

  let cloudStats: LogStatsOutput['stats'] | null = null;
  try {
    cloudStats = await getCloudLogStats();
  } catch {
    // Cloud stats unavailable
  }

  return {
    pwa: {
      total: pwaLogs.length,
      errors: pwaErrors.length,
      warnings: pwaWarnings.length,
    },
    cloudFunctions: cloudStats ?? null,
  };
}

/**
 * Get severity color class for UI
 */
export function getSeverityColor(severity: Severity): string {
  switch (severity) {
    case 'low':
      return 'text-gray-500 bg-gray-100';
    case 'medium':
      return 'text-yellow-700 bg-yellow-100';
    case 'high':
      return 'text-orange-700 bg-orange-100';
    case 'critical':
      return 'text-red-700 bg-red-100';
    default:
      return 'text-gray-500 bg-gray-100';
  }
}

/**
 * Get severity badge class for UI
 */
export function getSeverityBadge(severity: Severity): string {
  switch (severity) {
    case 'low':
      return 'bg-gray-100 text-gray-800';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800';
    case 'high':
      return 'bg-orange-100 text-orange-800';
    case 'critical':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Format timestamp for display
 */
export function formatLogTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
